import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { scrollToSection } from "@/lib/scroll";

const LINKS = [
  { id: "accueil", label: "Accueil" },
  { id: "galerie", label: "Galerie" },
  { id: "tarifs", label: "Tarifs" },
  { id: "temoignages", label: "Témoignages" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "border-b border-white/10 bg-[#08070d]/80 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 md:px-8">
        <Logo onClick={() => go("accueil")} />

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              type="button"
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="group relative text-sm font-medium text-[#a09bb5] transition-colors hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-amber-400 transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="nav-cta-commander"
            onClick={() => go("contact")}
            className="btn-holo hidden rounded-full px-5 py-2.5 text-sm font-semibold text-[#08070d] sm:block"
          >
            Commander une carte
          </button>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
            className="rounded-lg border border-white/10 p-2 text-white lg:hidden"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden border-b border-white/10 bg-[#08070d]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="rounded-lg px-3 py-3 text-left text-base font-medium text-[#a09bb5] transition-colors hover:bg-white/5 hover:text-white"
                >
                  {l.label}
                </button>
              ))}
              <button
                type="button"
                data-testid="nav-mobile-cta-commander"
                onClick={() => go("contact")}
                className="btn-holo mt-2 rounded-full px-5 py-3 text-sm font-semibold text-[#08070d]"
              >
                Commander une carte
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
